// La tabla de pagos (`.compact-table` del mock) con su barra de conteo y total encima.
import { Badge } from '@/kit';
import { IdText } from '@/shared/components/IdText';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import {
    EMPTY_PAYMENTS_TEXT,
    PAYMENT_CHANNEL_CHIP,
    PAYMENT_COLUMN_LABELS,
    formatPaymentAmount,
    formatPaymentDateTime,
    paymentCountLabel,
    sumPayments,
} from '../logic';
import type { CollectorPaymentRow } from '../types';

const [PAYMENT_ID, CUSTOMER, RECEIVED_AT, AMOUNT, CHANNEL, CREDIT_ID] = PAYMENT_COLUMN_LABELS;

const COLUMNS: DataTableColumn<CollectorPaymentRow>[] = [
    {
        key: 'paymentId',
        label: PAYMENT_ID,
        width: '78px',
        cell: (row) => <IdText value={row.paymentId} className="text-[10.5px]" />,
    },
    { key: 'customer', label: CUSTOMER, cell: (row) => row.customerName },
    {
        key: 'receivedAt',
        label: RECEIVED_AT,
        width: '110px',
        cell: (row) => (
            <span className="text-[10.5px] tabular-nums">
                {formatPaymentDateTime(row.receivedAt)}
            </span>
        ),
    },
    {
        key: 'amount',
        label: AMOUNT,
        width: '80px',
        numeric: true,
        cell: (row) => (
            <span className="font-semibold text-success">{formatPaymentAmount(row.amount)}</span>
        ),
    },
    {
        key: 'channel',
        label: CHANNEL,
        width: '70px',
        cellOverflow: 'nowrap',
        cell: (row) => <PaymentChannelChip row={row} />,
    },
    {
        key: 'creditId',
        label: CREDIT_ID,
        width: '78px',
        cell: (row) => <IdText value={row.creditId} className="text-[10.5px]" />,
    },
];

export function PaymentChannelChip({ row }: { row: CollectorPaymentRow }) {
    const chip = PAYMENT_CHANNEL_CHIP[row.channel];
    return (
        <Badge variant={chip.variant} size="xs">
            {chip.label}
        </Badge>
    );
}

export interface PaymentsTableProps {
    rows: readonly CollectorPaymentRow[];
    selectedPaymentId: string | null;
    onSelect: (paymentId: string) => void;
}

export function PaymentsTable({ rows, selectedPaymentId, onSelect }: PaymentsTableProps) {
    return (
        <>
            <div className="flex flex-none flex-wrap items-center gap-2 border-b border-border px-3 py-2 text-[11px] text-muted-foreground">
                <span>{paymentCountLabel(rows.length)}</span>
                <span className="flex-1" />
                <span>
                    Total:{' '}
                    <span className="font-semibold text-foreground">
                        {formatPaymentAmount(sumPayments(rows))}
                    </span>
                </span>
            </div>
            <DataTable
                columns={COLUMNS}
                rows={rows}
                rowKey={(row) => row.paymentId}
                caption="Pagos recibidos por el cobrador"
                empty={EMPTY_PAYMENTS_TEXT}
                isRowSelected={(row) => row.paymentId === selectedPaymentId}
                onRowClick={(row) => {
                    onSelect(row.paymentId);
                }}
            />
        </>
    );
}
